import {assert} from 'Asserts';
import {NoteLinkedFileTabState} from 'notebook/notes/attachments/NoteLinkedFileTab';
import {TFIFileMeta} from 'notebook/tfi/TFIConstants';
import {FromServerNoteLinkedFile, NoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export function create({location}: {location: string}): JQueryPromise<FromServerNoteLinkedFile> {
  return TaskRunner.runTask({
    url: '/1/api/note-linked-files/',
    contentType: 'application/json',
    type: 'POST',
    dataType: 'json',
    data: JSON.stringify({location}),
  });
}

export function transform({
  uuid,
  previewLocation,
  edits,
}: {
  uuid: string;
  previewLocation: string;
  edits?: string;
}): JQueryPromise<NoteLinkedFile> {
  return TaskRunner.runTask({
    url: `/1/api/note-linked-files/${uuid}/transform`,
    contentType: 'application/json',
    type: 'POST',
    dataType: 'json',
    data: JSON.stringify({previewLocation, edits}),
  });
}

export interface CreateLocalSyncCopyResponse {
  tfiFileMeta: TFIFileMeta;
}

export function createLocalSyncCopy({uuid}: {uuid: string}): JQueryPromise<CreateLocalSyncCopyResponse> {
  assert(uuid != null, 'Note linked file should have non-null UUID');
  return TaskRunner.runTask({
    url: `/1/api/note-linked-files/${uuid}:create-local-sync-copy`,
    type: 'POST',
  });
}

export function getDownloadURL({
  uuid,
  name,
  preview = '0',
}: {
  uuid: string;
  name: string;
  preview?: string;
}): string {
  return getDownloadURLFromEncodedName({uuid, encodedName: encodeURIComponent(name), preview});
}

export function getDownloadURLFromEncodedName({
  uuid,
  encodedName,
  preview = '0',
}: {
  uuid: string;
  encodedName: string;
  preview?: string;
}): string {
  return `/1/api/note-linked-files/${uuid}:download?name=${encodedName}&preview=${encodeURIComponent(preview)}`;
}

export function getPreviewURL(
  tabState: NoteLinkedFileTabState,
  fileName: string,
  noteLinkedFileId: string,
  isPreviewTransformed: boolean
): string {
  const previewType = tabState.previewType();
  const encodedUrl = previewType ? encodeURIComponent(tabState.previewTypeUrlMapping()[previewType]) : '';
  const id = previewType ? tabState.previewTypeIdMapping()[previewType] : '';
  const owningEntityApiId = tabState.owningEntityApiId();
  const encodedName = encodeURIComponent(fileName);
  const encodedSourceUrl = encodeURIComponent(window.location.pathname);
  const encodedAvailablePreviewTypes = encodeURIComponent(
    Object.keys(tabState.previewTypeUrlMapping()).join(',')
  );
  return `/preview/${owningEntityApiId}/${noteLinkedFileId}/${id}/${encodedName}/${isPreviewTransformed}/${previewType}/${encodedUrl}/${encodedSourceUrl}/${encodedAvailablePreviewTypes}`;
}

export async function getNoteLinkedFile({
  uuid,
  entryId,
}: {
  uuid: string;
  entryId: string;
}): Promise<FromServerNoteLinkedFile> {
  return await Net.getJSONPromise(`/1/api/entries/${entryId}/files/${uuid}`);
}

export function bulkGetNoteLinkedFilesByEntryId(noteLinkedFileIdsByEntryId: {
  [entryId: string]: Array<string>;
}): Promise<{[id: string]: FromServerNoteLinkedFile}> {
  return Net.postJSONPromise({
    url: '/1/api/note-linked-files:bulk-get',
    data: {noteLinkedFileIdsByEntryId},
  }).then((res: {noteLinkedFiles: {[id: string]: FromServerNoteLinkedFile}}) => res.noteLinkedFiles);
}
